import type { SortingState } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { ProductNameWithLabels } from '../../../../components/product-table/columns/product-name-with-labels-column';
import { Department, Type } from '../../../../components/product-table/columns/simple-columns';
import { Size } from '../../../../components/product-table/columns/size-column';
import type { SortColumnOption } from '../../../../components/sort-popover';
import { AnnualProfit } from '../components/annual-profit-column';
import { OpportunityCost } from '../components/opportunity-cost-column';
import { OutOfStockRisk } from '../components/out-of-stock-risk-column';
import { SavingsPotential90d } from '../components/savings-potential-column';

type SortMode = 'default' | 'out-of-stock-risk' | 'opportunity-cost' | 'annual-profit';

const SORT_MODE_TO_COLUMN_ID: Record<SortMode, string> = {
  'out-of-stock-risk': OutOfStockRisk.id,
  'opportunity-cost': OpportunityCost.id,
  'annual-profit': AnnualProfit.id,
  default: '__default__',
};

const COLUMN_ID_TO_SORT_MODE = Object.fromEntries(
  Object.entries(SORT_MODE_TO_COLUMN_ID).map(([mode, id]) => [id, mode as SortMode])
);

export const SORT_COLUMN_OPTIONS: SortColumnOption[] = [
  { id: '__default__', label: 'Default', hideDirection: true },
  { id: OutOfStockRisk.id, label: 'Out of Stock Risk', hideDirection: true },
  { id: OpportunityCost.id, label: 'Sales Opportunity Cost', hideDirection: true },
  { id: AnnualProfit.id, label: 'Most Profitable', hideDirection: true },
];

export function useSortedOrder(reorderColumnId: string, isBuyup: boolean) {
  const [sortMode, setSortMode] = useState<SortMode>('default');

  const defaultSorting = useMemo<SortingState>(() => {
    if (isBuyup) {
      return [{ id: SavingsPotential90d.id, desc: true }];
    }

    return [
      { id: reorderColumnId, desc: true },
      { id: Department.id, desc: false },
      { id: Size.id, desc: true },
      { id: Type.id, desc: false },
      { id: ProductNameWithLabels.id, desc: false },
    ];
  }, [isBuyup, reorderColumnId]);

  const sorting = useMemo<SortingState>(() => {
    if (sortMode === 'default') {
      return defaultSorting;
    }

    return [
      { id: reorderColumnId, desc: true },
      { id: SORT_MODE_TO_COLUMN_ID[sortMode], desc: true },
    ];
  }, [sortMode, defaultSorting, reorderColumnId]);

  const currentSortColumnId = SORT_MODE_TO_COLUMN_ID[sortMode];

  const sortPopoverSorting = useMemo<SortingState>(
    () => [{ id: currentSortColumnId, desc: true }],
    [currentSortColumnId]
  );

  const handleSortPopoverChange = useCallback((newSorting: SortingState) => {
    const id = newSorting[0]?.id ?? '__default__';
    setSortMode(COLUMN_ID_TO_SORT_MODE[id] ?? 'default');
  }, []);

  return { sorting, sortPopoverSorting, handleSortPopoverChange };
}
